import { useEffect, useRef, useState } from "react";
import LiveOrdersTables from "./LiveOrdersTables";
import { useNotification } from "../../toster-notification/NotificationContext";
import { useParams } from "react-router-dom";
import { LiveOrderTableDTO } from "../../../models/table/table.response";
import { tableService } from "../../../services/tableService";
import { handleErrorNotification } from "../../../utils/errorNotificatioMessage";
import Loader from "../../shared/Loader";
import { TableStatusType } from "../../../models/base/TableStatusType";
import { HandIcon, HandRedIcon } from "../../../assets/icons";
import TableInfoView from "./TableInfoView";
import { connection, startConnection } from "../../../services/signalRConnection";
import { requestBrowserNotificationPermission, playNotificationSound, showBrowserNotification, unlockNotificationSound } from "../../../notification/notificationService";


const LiveOrdersView1: React.FC = () => {

    const [tables, setTables] = useState<LiveOrderTableDTO[]>([]);
    const tablesRef = useRef<LiveOrderTableDTO[]>([]);
    const [selectedTableId, setSelectedTableId] = useState<string>("");
    const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>("");
    const callWaiterIdsRef = useRef<string[]>([]);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
    const selectedTableIdRef = useRef<string>("");

    const [notificationsEnabled, setNotificationsEnabled] = useState(Notification.permission === "granted");
    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotification();
    const { localId } = useParams<{ localId: string }>();


    useEffect(() => {
        fetchTables();
        fetchBackgroundImage();
    }, [localId]);


    useEffect(() => {
        selectedTableIdRef.current = selectedTableId;
    }, [selectedTableId]);

    useEffect(() => {
        tablesRef.current = tables;
    }, [tables]);

    useEffect(() => {
        if (!localId) {
            return;
        }

        const handleBillRequested = async (
            orderId: string,
            tableId: string
        ) => {
            await handleOrderNotification(
                "bill-requested",
                "Zatražen račun",
                `Porudžbina #${orderId.slice(0, 8)} je zatražila račun.`,
                orderId
            );

            fetchTable(tableId);
        };

        const handleNewOrder = async (orderId: string) => {
            await handleOrderNotification(
                "new-order",
                "Nova porudžbina",
                `Stigla je nova porudžbina #${orderId.slice(0, 8)}.`,
                orderId
            );

            fetchTables();
        };

        const setup = async () => {
            await startConnection();
            await connection.invoke<void>("JoinOrderDineInMonitoring", localId);
            connection.on("BillRequested", handleBillRequested);
            connection.on("NewDineInOrder", handleNewOrder);
        };

        setup();

        return () => {
            connection.off("BillRequested", handleBillRequested);
            connection.off("NewDineInOrder", handleNewOrder);
        };
    }, [localId]);

    useEffect(() => {
        const handler = (tableId: string) => {

            // Treba promeniti naziv jer se ovo izvrsava pri kreiranju porudzbine
            let tableNumber = tablesRef.current.find(t => t.id === tableId)?.number;
            if (tableNumber) {
                notify("info", `Porudžbina za sto ${tableNumber} je kreirana.`, "Nova porudžbina");
            } else {
                notify("info", `Porudžbina je kreirana.`, "Nova porudžbina");
            }
            fetchTable(tableId);
        };

        const setup = async () => {
            await startConnection();
            await connection.invoke<void>("JoinTablesMonitoringGroup", localId);
            connection.on("TableStatusChange", handler);
        };

        setup();

        return () => {
            connection.off("TableStatusChange", handler);
        };

    }, [localId]);


    useEffect(() => {
        const handler = (tableId: string) => {
            let tableNumber = tablesRef.current.find(t => t.id === tableId)?.number;
            notify("info", `Sto ${tableNumber || tableId}`, "Poziv konobara");
            setTables(prev => prev.map(table => table.id === tableId ? { ...table, callWaiter: true } : table))
        };

        const setup = async () => {
            await startConnection();

            const ids = await connection.invoke<string[]>("JoinCallWaiterAsync", localId);
            callWaiterIdsRef.current = ids;
            setTables(prev => prev.map(table => ({ ...table, callWaiter: ids.includes(table.id) })));

            connection.on("CallWaiter", handler);
        };

        setup();

        return () => {
            connection.off("CallWaiter", handler);
        };

    }, [localId]);


    const waiterReceiveAsync = async (tableId: string) => {
        if (tableId == null || localId == null)
            return;

        await startConnection();
        await connection.invoke<void>("WaiterReceiveAsync", localId, tableId)

    }

    const fetchBackgroundImage = async () => {
        if (!localId) {
            notify("error", "Local ID nije prosleđen");
            return;
        }

        try {
            const response = await tableService.getBackgroundImage(localId);
            if (response.success && response.data) {
                setBackgroundImageUrl(response.data);
            } else {
                notify("error", "Greška pri učitavanju pozadine", response.message || "Došlo je do greške pri učitavanju pozadine.");
            }
        } catch (error: any) {
            notify("error", "Greška pri učitavanju pozadine", "Došlo je do greške pri učitavanju pozadine.");
            handleErrorNotification(error, notify);
            console.error("Error fetching background image:", error);
        }
    };

    const fetchTable = async (tableId: string) => {

        if (!localId) {
            notify("error", "Local ID nije prosledjen");
            return;
        }

        try {
            const response = await tableService.getLiveOrdersTableByLocalId(localId, tableId);

            if (response.success && response.data) {
                const updatedTable = response.data;
                if (callWaiterIdsRef.current.includes(tableId)) {
                    updatedTable.callWaiter = true;
                }
                setTables(prev => [...prev.filter(t => t.id !== tableId), updatedTable]);
                if (selectedTableIdRef.current === tableId) {
                    setRefreshTrigger(prev => prev + 1);
                }
            } else {
                notify("error", "Greška pri učitavanju stola", response.message || "Došlo je do greške pri učitavanju stola.");

            }
        } catch (error: any) {
            notify("error", "Greška pri učitavanju stola", "Došlo je do greške pri učitavanju stola.");
            handleErrorNotification(error, notify);
            console.error("Error fetching tables:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTables = async () => {
        if (!localId) {
            notify("error", "Local ID nije prosleđen");
            return;
        }

        setLoading(true);
        try {
            const response = await tableService.getLiveOrdersTablesByLocalId(localId);

            if (response.success && response.data) {
                setTables(response.data.map(table => ({ ...table, callWaiter: callWaiterIdsRef.current.includes(table.id) })));
            } else {
                notify("error", "Greška pri učitavanju stolova", response.message || "Došlo je do greške pri učitavanju stolova.");

            }
        } catch (error: any) {
            notify("error", "Greška pri učitavanju stolova", "Došlo je do greške pri učitavanju stolova.");
            handleErrorNotification(error, notify);
            console.error("Error fetching tables:", error);
        } finally {
            setLoading(false);
        }
    };

    const enableNotifications = async () => {

        const granted = await requestBrowserNotificationPermission();

        setNotificationsEnabled(granted);

        if (granted) {

            // Kratko pokrenemo zvuk kao rezultat user interaction-a.
            // Tako browser zna da korisnik dozvoljava audio.
            await unlockNotificationSound();

            notify(
                "success",
                "Obaveštenja uključena",
                "Primaćete zvučna obaveštenja za nove porudžbine."
            );
        }
    };

    const handleOrderNotification = async (
        type:
            | "new-order"
            | "bill-requested"
            | "order-ready"
            | "order-cancelled",
        title: string,
        message: string,
        orderId?: string
    ) => {

        // 1. Zvuk
        await playNotificationSound(type);

        // 2. Tvoja postojeća notifikacija unutar React aplikacije
        notify(
            type === "order-cancelled" ? "error" : "info",
            title,
            message
        );

        // 3. System notification samo ako korisnik
        // trenutno ne gleda aplikaciju
        if (document.visibilityState !== "visible") {

            await showBrowserNotification({
                title,
                body: message,

                tag: orderId
                    ? `${type}-${orderId}`
                    : type,

                // prilagodi tvojoj ruti
                url: `/admin/local/${localId}/orders`
            });
        }
    };


    if (loading) {
        return <Loader />;
    }

    return (
        <>
            {!notificationsEnabled && (
                <button onClick={enableNotifications}>
                    🔔 Uključi obaveštenja
                </button>
            )}
{/* 
            <button onClick={() => handleOrderNotification("new-order", "Test zvuka", "Testna notifikacija")}> 
                🔊 Testiraj zvuk
            </button> */}

            <section className="live-order-view1-section">
                <div className="w-layout-blockcontainer container live-order-main w-container">
                    <div className="live-order-main-wrapper">
                        <div className="tables-status-layout">
                            <div className="lo-status-list">
                                <div id="w-node-_784b75b9-7580-94fe-59b3-83eda507705e-1cee91cf" className="admin-settings-box-div">
                                    <div className="lo-status-header-div">
                                        <div>Slobodno</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info free"></div>
                                        <div>{tables.filter(t => t.currentStatus === TableStatusType.Free).length}</div>
                                    </div>
                                </div>
                                <div className="admin-settings-box-div">
                                    <div className="lo-status-header-div">
                                        <div>Poručeno</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info ordered"></div>
                                        <div>{tables.filter(t => t.currentStatus === TableStatusType.Ordered).length}</div>
                                    </div>
                                </div>
                                <div className="admin-settings-box-div">
                                    <div className="lo-status-header-div">
                                        <div>Primljeno</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info recived"></div>
                                        <div>{tables.filter(t => t.currentStatus === TableStatusType.Recived).length}</div>
                                    </div>
                                </div>
                                <div className="admin-settings-box-div">
                                    <div className="lo-status-header-div">
                                        <div>Usluženo</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info served"></div>
                                        <div>{tables.filter(t => t.currentStatus === TableStatusType.Served).length}</div>
                                    </div>
                                </div>
                                <div className="admin-settings-box-div">
                                    <div className="lo-status-header-div">
                                        <div>Račun</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info bill"></div>
                                        <div>{tables.filter(t => t.currentStatus === TableStatusType.Bill).length}</div>
                                    </div>
                                </div>
                                <div className="admin-settings-box-div">
                                    <div className="lo-status-header-div">
                                        <div>Plaćeno</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info paid"></div>
                                        <div className="text-block-16">{tables.filter(t => t.currentStatus === TableStatusType.Paid).length}</div>
                                    </div>
                                </div>
                                <div className="admin-settings-box-div red-bg">
                                    <div className="lo-status-header-div">
                                        <div>
                                            Pozivi konobara<br />
                                        </div>
                                    </div>
                                    <div className="lo-status-block">
                                        <img loading="lazy" src={HandRedIcon} alt="" className="icon-size-24" />
                                        <div className="text-block-16">{tables.filter(t => t.callWaiter === true).length}</div>
                                    </div>
                                </div>
                            </div>

                            <LiveOrdersTables
                                tables={tables}
                                disablePositioning={true}
                                onSelectedTableChange={setSelectedTableId}
                                backgroundImageUrl={backgroundImageUrl + "@ul.webp"}
                            />
                        </div>

                        <TableInfoView
                            selectedTableId={selectedTableId}
                            refreshTrigger={refreshTrigger}
                            onStatusChange={() => fetchTable(selectedTableId)}
                            isCallWaiter={tables.some(t => t.id === selectedTableId && t.callWaiter === true)}
                            onWaiterReceive={(tableId) => {
                                setTables(prev => prev.map(t => t.id === tableId ? { ...t, callWaiter: false } : t));
                                waiterReceiveAsync(tableId);
                            }}
                        />

                    </div>
                </div>
            </section>
        </>
    )
};

export default LiveOrdersView1;